import React, { memo, useMemo } from 'react';
import { View, Image, StyleSheet } from 'react-native';
import {
  withPopUp,
  Space,
  Text,
  IconDownload,
  IconButton,
} from '@redlink/components/atoms';
import { FormSection, CopyUrl } from '@redlink/components/molecules';
import { Form } from '@redlink/components/organisms';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { createUseStyle } from '@redlink/assets/styles';
import { useQuery, getPageProfileQuery } from '@redlink/services';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import { saveAs } from 'file-saver';
import {
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinIcon,
  TelegramIcon,
  TwitterIcon,
  WhatsappIcon,
} from 'react-share';

export const SharePage = withPopUp(
  memo(({ onDismiss }) => {
    const { dictionary } = useLocale();
    const { styles, colors } = useStyle();
    const { getPageUrl } = useValues();

    const {
      loading: getPageProfileIsBusy,
      data: { PageProfile: { pageName, shareImageUrl } = {} },
    } = useQuery(getPageProfileQuery);

    const pageUrl = useMemo(() => getPageUrl(pageName), [pageName]);

    return (
      <DataEntryModalTemplate
        previewContainerStyle={styles.previewContainerStyle}
      >
        <Form
          title={dictionary.share}
          isBusy={getPageProfileIsBusy}
          actions={[
            {
              title: dictionary.back,
              variant: 'secondary',
              onPress: onDismiss,
            },
          ]}
        >
          <FormSection title={dictionary.share}>
            <View>
              <Text style={styles.titleText}> {dictionary.usingRedlink}</Text>
              <Text style={styles.descriptionText}>
                {dictionary.shareDescriptionLine1}
              </Text>
              <Text style={styles.descriptionText}>
                {dictionary.shareDescriptionLine2}
              </Text>
              <Space />
            </View>
            <CopyUrl url={pageUrl} />
            <Space size={30} />
            <Text style={styles.descriptionText}>
              {dictionary.shareDescriptionLine3}
            </Text>
            <Space />
            <View style={styles.iconsContainer}>
              <TwitterShareButton url={pageUrl}>
                <View style={styles.buttonContainer}>
                  <TwitterIcon size={50} borderRadius={15} />
                  <Text style={styles.iconText}>Twitter</Text>
                </View>
              </TwitterShareButton>
              <WhatsappShareButton url={pageUrl}>
                <View style={styles.buttonContainer}>
                  <WhatsappIcon size={50} borderRadius={15} />
                  <Text style={styles.iconText}>WhatsApp</Text>
                </View>
              </WhatsappShareButton>
              <TelegramShareButton url={pageUrl}>
                <View style={styles.buttonContainer}>
                  <TelegramIcon size={50} borderRadius={15} />
                  <Text style={styles.iconText}>Telegram</Text>
                </View>
              </TelegramShareButton>
              <LinkedinShareButton url={pageUrl}>
                <View style={styles.buttonContainer}>
                  <LinkedinIcon size={50} borderRadius={15} />
                  <Text style={styles.iconText}>Linkedin</Text>
                </View>
              </LinkedinShareButton>
              {shareImageUrl && (
                <View style={styles.buttonContainer}>
                  <IconButton
                    style={styles.downloadButton}
                    onPress={() => {
                      saveAs(shareImageUrl, `redlink_${pageName}.png`);
                    }}
                  >
                    <IconDownload size={30} color={colors.white} />
                  </IconButton>
                  <Text style={styles.iconText}>Download</Text>
                </View>
              )}
            </View>
          </FormSection>
        </Form>
        {shareImageUrl && (
          <Image
            source={{ uri: shareImageUrl }}
            style={StyleSheet.absoluteFillObject}
            resizeMode="contain"
          />
        )}
      </DataEntryModalTemplate>
    );
  })
);

const useStyle = createUseStyle(({ colors, size, shadow, ltrFlexDirection }) => ({
  iconsContainer: {
    flexDirection: ltrFlexDirection,
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  iconText: {
    fontSize: 12,
    color: colors.gray23,
    lineHeight: 24,
  },
  titleText: {
    fontSize: 16,
    color: colors.gray23,
    lineHeight: 24,
    fontWeight: '500',
  },
  descriptionText: {
    fontSize: 14,
    color: colors.gray21,
    lineHeight: 24,
    fontWeight: '400',
  },
  previewContainerStyle: {
    backgroundColor: colors.red1,
  },
  downloadButton: {
    width: 50,
    height: 50,
    borderRadius: 15,
    backgroundColor: colors.black4,
  },
  buttonContainer: {
    alignItems: 'center',
  },
}));
